<cac-dialog-layout
  [title]="'Add Product to Order'"
  subtitle="Search and add a product to this order"
  (onSubmit)="onSubmit($event)"
  [submitBtn]="'Add Product'"
>
  <div class="p-6 space-y-6">
    <!-- Product Search -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700">Search Product</label>
      <app-lazy-dropdown
        [multiple]="false"
        [searchable]="true"
        [clearable]="true"
        placeholder="Search and select a product..."
        hint="Search by product name to add to the order"
        [apiAdapter]="productApiAdapter"
        [itemMapper]="productItemMapper"
        idsFilterKey="ProductIds"
        searchField="EnglishName"
        (selectionChange)="onProductSelectionChange($event)"
      />
    </div>

    <!-- Loading State -->
    @if (loadingProduct()) {
      <div class="flex items-center justify-center py-8">
        <div class="text-blue-600 flex items-center gap-2">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          Loading product details...
        </div>
      </div>
    }

    <!-- Product Details -->
    @if (!loadingProduct() && selectedProduct()) {
      <div class="border rounded-lg p-4 space-y-4">
        <!-- Product Header -->
        <div class="flex items-start gap-4">
          <!-- Product Image -->
          @if (selectedProduct()!.images.length > 0) {
            <img
              [src]="selectedProduct()!.images[0].url"
              class="w-16 h-16 object-cover rounded-lg border border-gray-200"
            >
          } 

          <!-- Product Info -->
          <div class="flex-1">
            <h3 class="font-medium text-gray-900">
              {{ selectedProduct()!.name.english || selectedProduct()!.name.arabic }}
            </h3>
            @if (selectedProduct()!.name.arabic && selectedProduct()!.name.english !== selectedProduct()!.name.arabic) {
              <p class="text-sm text-gray-600">{{ selectedProduct()!.name.arabic }}</p>
            }
            @if (selectedProduct()!.model) {
              <p class="text-sm text-gray-500">Model: {{ selectedProduct()!.model }}</p>
            }
          </div>
        </div>


        <!-- Variant Selection (for variable products) -->
        @if (selectedProduct()!.type === 1 && selectedProduct()!.variants.length > 1) {
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Select Variant</label>
            <select
              class="form-select w-full p-2 border border-gray-300 rounded-md"
              [value]="selectedVariant()?.id || ''"
              (change)="onVariantSelectionChange(+$any($event.target).value)"
            >
              <option value="">Choose a variant...</option>
              @for (variant of getAvailableVariants(); track variant.id) {
                <option [value]="variant.id">
                  {{ getVariantDisplayText(variant) }}
                  @if (variant.isManageableStock) {
                    - Stock: {{ variant.stock }}
                  }
                </option>
              }
            </select>
          </div>
        }

        <!-- Quantity and Price Fields -->
        @if (selectedVariant()) {
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Quantity -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">Quantity</label>
              <input
                type="number"
                class="form-input w-full p-2 border border-gray-300 rounded-md"
                [class.border-red-300]="form.controls.quantity.invalid && form.controls.quantity.touched"
                [formControl]="form.controls.quantity"
                min="1"
              >

              <!-- Stock Info (informational only) -->
              @if (selectedVariant()!.isManageableStock) {
                <div class="text-sm text-blue-600">
                  Available stock: {{ selectedVariant()!.stock }}
                </div>
              }

              <!-- Validation Error -->
              @if (form.controls.quantity.invalid && form.controls.quantity.touched) {
                <div class="text-sm text-red-600">
                  @if (form.controls.quantity.hasError('required')) {
                    Quantity is required
                  } @else if (form.controls.quantity.hasError('min')) {
                    Minimum quantity is 1
                  }
                </div>
              }
            </div>

            <!-- Item Price (readonly) -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">Item Price</label>
              <input
                type="text"
                class="form-input w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                [value]="getItemPrice() | kwdCurrency"
                readonly
              >
              @if (selectedVariant()!.salePrice && selectedVariant()!.salePrice! < selectedVariant()!.regularPrice) {
                <div class="text-sm text-gray-500 line-through">
                  {{ selectedVariant()!.regularPrice | kwdCurrency }}
                </div>
              }
            </div>

            <!-- Total Price (readonly) -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">Total Price</label>
              <input
                type="text"
                class="form-input w-full p-2 border border-gray-300 rounded-md bg-gray-50 font-medium"
                [value]="getTotalPrice() | kwdCurrency"
                readonly
              >
            </div>
          </div>
        }
      </div>
    }
  </div>
</cac-dialog-layout>
